import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import aspectRatios, { AspectRatioIcon } from "../../constants/aspectRatios";
import resolutionMap, { RESOLUTION_OPTIONS } from "../../constants/resolutionMap";
import AngleViewDropdown from '../../components/AngleViewDropdown';
import { generateRoomVariation } from "../../services/geminiService";
import { generateWithFlux, isFluxModel } from "../../services/fluxService";
import { getUserBoards, getBoardPins } from "../../services/pinterestService";
import { useProjects } from "../../contexts/ProjectContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from "../../services/supabaseService";
import { supabase } from "../../lib/supabase";
import toast, { Toaster } from "react-hot-toast";
import UploadedImageThumb from "../../components/UploadedImageThumb";
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import RoomTypeDropdown from '../../components/RoomTypeDropdown';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import { downloadImage } from '../../utils/downloadUtils';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';

// ─────────────────────────────────────────────
// Pinterest Modal
// ─────────────────────────────────────────────
function PinterestModal({ onClose, onSelectImage }) {
  const [view, setView] = useState("boards"); // "boards" | "pins"
  const [boards, setBoards] = useState([]);
  const [pins, setPins] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loadingPins, setLoadingPins] = useState(false);
  const [boardsError, setBoardsError] = useState("");
  const [pinsError, setPinsError] = useState("");
  const [selectedPin, setSelectedPin] = useState(null);

  // Load boards on mount
  useEffect(() => {
    const fetchBoards = async () => {
      setLoadingBoards(true);
      setBoardsError("");
      try {
        const data = await getUserBoards();
        setBoards(data);
      } catch (err) {
        setBoardsError(err.message);
      } finally {
        setLoadingBoards(false);
      }
    };
    fetchBoards();
  }, []);

  // Load pins when a board is selected
  const handleSelectBoard = async (board) => {
    setSelectedBoard(board);
    setView("pins");
    setPins([]);
    setPinsError("");
    setSelectedPin(null);
    setLoadingPins(true);
    try {
      const data = await getBoardPins(board.id, 50);
      setPins(data);
    } catch (err) {
      setPinsError(err.message);
    } finally {
      setLoadingPins(false);
    }
  };

  const handleConfirmPin = () => {
    if (!selectedPin) return;
    onSelectImage(selectedPin.imageUrl, selectedPin.title);
    onClose();
    toast.success(`Pin added as room reference!`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div
        className="glassmorphic-card rounded-3xl max-w-2xl w-full relative flex flex-col"
        style={{ maxHeight: "85vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            {view === "pins" && (
              <button
                onClick={() => {
                  setView("boards");
                  setSelectedPin(null);
                }}
                className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all"
              >
                <Icon name="ArrowLeft" size={16} />
              </button>
            )}
            <div className="w-8 h-8 rounded-full bg-[#E60023] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm leading-none">
                P
              </span>
            </div>
            <div>
              <h3 className="text-white font-bold text-base leading-tight">
                {view === "boards"
                  ? "Your Pinterest Boards"
                  : selectedBoard?.name}
              </h3>
              <p className="text-white/40 text-xs mt-0.5">
                {view === "boards"
                  ? `${boards.length} board${boards.length !== 1 ? "s" : ""} found`
                  : `${pins.length} pin${pins.length !== 1 ? "s" : ""} · tap one to select`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors p-1"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {/* ── BOARDS VIEW ── */}
          {view === "boards" && (
            <>
              {loadingBoards && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Icon
                    name="Loader"
                    size={32}
                    className="text-orange-500 animate-spin"
                  />
                  <p className="text-white/50 text-sm">
                    Loading your boards...
                  </p>
                </div>
              )}

              {boardsError && !loadingBoards && (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                  <Icon name="AlertCircle" size={36} className="text-red-400" />
                  <div>
                    <p className="text-red-400 text-sm font-medium mb-1">
                      Failed to load boards
                    </p>
                    <p className="text-white/40 text-xs max-w-xs">
                      {boardsError}
                    </p>
                  </div>
                  <p className="text-white/25 text-[11px]">
                    Make sure{" "}
                    <code className="text-white/40 bg-white/10 px-1 py-0.5 rounded">
                      VITE_PINTEREST_ACCESS_TOKEN
                    </code>{" "}
                    is set in your .env
                  </p>
                </div>
              )}

              {!loadingBoards && !boardsError && boards.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                  <Icon name="Layout" size={36} className="text-white/20" />
                  <p className="text-white/50 text-sm">
                    No boards found on this account
                  </p>
                </div>
              )}

              {!loadingBoards && !boardsError && boards.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {boards.map((board) => (
                    <button
                      key={board.id}
                      onClick={() => handleSelectBoard(board)}
                      className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-orange-500/40 transition-all text-left"
                    >
                      {/* Cover */}
                      <div className="w-full h-28 bg-white/5 overflow-hidden relative">
                        {board.imageUrl ? (
                          <img
                            src={board.imageUrl}
                            alt={board.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon
                              name="Layout"
                              size={28}
                              className="text-white/20"
                            />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Icon
                            name="ArrowRight"
                            size={20}
                            className="text-white drop-shadow"
                          />
                        </div>
                      </div>
                      {/* Info */}
                      <div className="p-3">
                        <p className="text-white text-xs font-semibold truncate">
                          {board.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-white/40 text-[10px]">
                            {board.pinCount} pins
                          </p>
                          {board.privacy === "secret" && (
                            <span className="text-yellow-500/60 text-[9px] border border-yellow-500/30 rounded px-1">
                              secret
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── PINS VIEW ── */}
          {view === "pins" && (
            <>
              {loadingPins && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Icon
                    name="Loader"
                    size={32}
                    className="text-orange-500 animate-spin"
                  />
                  <p className="text-white/50 text-sm">
                    Loading pins from "{selectedBoard?.name}"...
                  </p>
                </div>
              )}

              {pinsError && !loadingPins && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                  <Icon name="AlertCircle" size={36} className="text-red-400" />
                  <p className="text-red-400 text-sm font-medium">
                    Failed to load pins
                  </p>
                  <p className="text-white/40 text-xs max-w-xs">{pinsError}</p>
                </div>
              )}

              {!loadingPins && !pinsError && pins.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                  <Icon name="Image" size={36} className="text-white/20" />
                  <p className="text-white/50 text-sm">
                    No pins found in this board
                  </p>
                </div>
              )}

              {!loadingPins && !pinsError && pins.length > 0 && (
                <>
                  {selectedPin && (
                    <div className="mb-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center gap-2">
                      <Icon
                        name="Check"
                        size={14}
                        className="text-orange-400 flex-shrink-0"
                      />
                      <p className="text-orange-300 text-xs">
                        1 pin selected — click "Use this image" below to add it
                      </p>
                    </div>
                  )}
                  <div className="columns-2 sm:columns-3 gap-3 space-y-3">
                    {pins.map((pin) => (
                      <button
                        key={pin.id}
                        onClick={() =>
                          setSelectedPin(
                            selectedPin?.id === pin.id ? null : pin,
                          )
                        }
                        className={`w-full rounded-xl overflow-hidden relative group transition-all break-inside-avoid block ${
                          selectedPin?.id === pin.id
                            ? "ring-2 ring-orange-500 ring-offset-2 ring-offset-black"
                            : "hover:opacity-80"
                        }`}
                      >
                        {pin.imageUrl ? (
                          <img
                            src={pin.imageUrl}
                            alt={pin.alt}
                            className="w-full h-auto block"
                          />
                        ) : (
                          <div className="w-full h-32 bg-white/5 flex items-center justify-center">
                            <Icon
                              name="Image"
                              size={24}
                              className="text-white/20"
                            />
                          </div>
                        )}
                        {/* Selected checkmark */}
                        {selectedPin?.id === pin.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center shadow-lg">
                            <Icon
                              name="Check"
                              size={13}
                              className="text-white"
                            />
                          </div>
                        )}
                        {/* Hover title */}
                        {pin.title && pin.title !== "Untitled" && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-[10px] line-clamp-2 text-left">
                              {pin.title}
                            </p>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex gap-3 flex-shrink-0">
          {view === "boards" ? (
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white text-sm font-medium transition-all"
            >
              Cancel
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setView("boards");
                  setSelectedPin(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white text-sm font-medium transition-all"
              >
                Back to Boards
              </button>
              <button
                onClick={handleConfirmPin}
                disabled={!selectedPin}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Icon name="Plus" size={16} />
                Use this image
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const RoomVariationGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [roomImages, setRoomImages] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [selectedModel, setSelectedModel] = useState("Gemini 3.1 Flash Image Preview");
  const [selectedImageSize, setSelectedImageSize] = useState("Auto");
  const [prompt, setPrompt] = useState("");
  const promptRef = useRef(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedRoomSize, setSelectedRoomSize] = useState(null);
  const [selectedAngleView, setSelectedAngleView] = useState("--");
  const [angleViewShortcut, setAngleViewShortcut] = useState("top-down");
  const [showPinterestModal, setShowPinterestModal] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState("--");
  const [isGenerating, setIsGenerating] = useState(false);
  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();
  const [generationError, setGenerationError] = useState("");
  const [buttonLoading, setButtonLoading] = useState(null);
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);

  // Virtual Tool Guide state
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const guideBubblePosRef = useRef({ top: '50%', left: '50%' });

  const pinterestDesktopRef = useRef(null);
  const pinterestMobileRef = useRef(null);
  const uploadDesktopRef = useRef(null);
  const uploadMobileRef = useRef(null);
  const roomSizeDesktopRef = useRef(null);
  const roomSizeMobileRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);
  const angleToggleDesktopRef = useRef(null);
  const angleToggleMobileRef = useRef(null);
  const roomTypeDesktopRef = useRef(null);
  const roomTypeMobileRef = useRef(null);
  const angleViewDesktopRef = useRef(null);
  const angleViewMobileRef = useRef(null);
  const modelDesktopRef = useRef(null);
  const modelMobileRef = useRef(null);
  const aspectRatioDesktopRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionDesktopRef = useRef(null);
  const resolutionMobileRef = useRef(null);
  const promptDesktopRef = useRef(null);
  const promptMobileRef = useRef(null);
  const generateBtnDesktopRef = useRef(null);
  const generateBtnMobileRef = useRef(null);
  const resultDesktopRef = useRef(null);
  const resultMobileRef = useRef(null);

  const totalGuideSteps = 13;

  const scrollGuideTarget = (step) => {
    const desktopRefs = [pinterestDesktopRef, uploadDesktopRef, roomSizeDesktopRef, assetLibraryDesktopRef, angleToggleDesktopRef, roomTypeDesktopRef, angleViewDesktopRef, modelDesktopRef, aspectRatioDesktopRef, resolutionDesktopRef, promptDesktopRef, generateBtnDesktopRef, resultDesktopRef];
    const mobileRefs = [pinterestMobileRef, uploadMobileRef, roomSizeMobileRef, assetLibraryMobileRef, angleToggleMobileRef, roomTypeMobileRef, angleViewMobileRef, modelMobileRef, aspectRatioMobileRef, resolutionMobileRef, promptMobileRef, generateBtnMobileRef, resultMobileRef];
    const ref = window.innerWidth >= 768 ? desktopRefs[step - 1]?.current : mobileRefs[step - 1]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const openGuide = () => {
    setGuideStep(1);
    setGuideVisible(false);
    setGuideAnimating(false);
    setShowGuide(true);
    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setGuideVisible(true));
      });
    }, 80);
  };

  const nextGuideStep = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep >= 4;
    if (needsScroll) {
      scrollGuideTarget(nextStep);
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 400);
      } else {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 100);
      }
    }, 700);
  };

  const closeGuide = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
      setGuideAnimating(false);
    }, 700);
  };

  const MAX_PROMPT_LENGTH = 1600;
  const MAX_IMAGES = 1;

  const getImageDimensions = () => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[selectedResolution]) {
      return ratioMap[selectedResolution];
    }
    const defaultDimensions = {
      Auto: { width: 1024, height: 1024 },
      "1:1": { width: 2048, height: 2048 },
      "4:3": { width: 2048, height: 1536 },
      "3:2": { width: 2048, height: 1365 },
      "16:9": { width: 2048, height: 1152 },
      "21:9": { width: 2048, height: 878 },
      "5:4": { width: 2048, height: 1638 },
      "2:3": { width: 1365, height: 2048 },
      "9:16": { width: 1152, height: 2048 },
      "3:4": { width: 1536, height: 2048 },
      "4:5": { width: 1638, height: 2048 },
    };
    return defaultDimensions[selectedImageSize] || { width: 1024, height: 1024 };
  };

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize] || resolutionMap['1:1'];
    return ratioMap ? ratioMap[resKey] || { width: 1024, height: 1024 } : { width: 1024, height: 1024 };
  };

  // Models constant removed - using shared ModelSelectionDropdown component
  const roomSizes = [
    { id: "small", label: "Small", size: "≤ 12 m²" },
    { id: "medium", label: "Medium", size: "13 – 25 m²" },
    { id: "large", label: "Large", size: "26 – 40 m²" },
    { id: "extra-large", label: "Extra-Large", size: "40+ m²" },
  ];
  const roomTypes = [
    "--",
    "Bedroom",
    "Living room",
    "Meeting room",
    "Bathroom",
    "Cabinet",
    "Kitchen",
    "Reception",
    "Lounge",
    "Bar",
    "Terrace",
    "Co-working area",
    "Restaurant",
  ];

  // Called when user picks a pin — adds it to roomImages
  const handlePinterestImageSelect = useCallback((imageUrl) => {
    setRoomImages((prev) => {
      if (prev.length >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images allowed.`);
        return prev;
      }
      return [...prev, imageUrl];
    });
  }, []);

  const handleRoomImageUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    if (!files.length) return;
    const remaining = Math.max(0, MAX_IMAGES - roomImages.length);
    if (remaining === 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed.`);
      e.target.value = '';
      return;
    }
    const allowed = files.slice(0, remaining);
    if (allowed.length < files.length) {
      toast(`Only ${remaining} more image${remaining === 1 ? '' : 's'} can be added (limit: ${MAX_IMAGES}).`, { icon: '⚠️' });
    }
    const imageUrls = (await Promise.all(allowed.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
    setRoomImages((prev) => [...prev, ...imageUrls]);
    e.target.value = '';
  };

  const handleRemoveRoomImage = (idx) => {
    setRoomImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAngleViewShortcut = (shortcut) => {
    setAngleViewShortcut(shortcut);
    if (shortcut === "frontal")
      setSelectedAngleView("Straight-On Eye-Level View");
    else if (shortcut === "top-down")
      setSelectedAngleView("Top-Down Flat View (90°)");
  };

  const resizeImageToCorrectDimensions = async (imageUrl, targetDimensions) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        canvas.width = targetDimensions.width;
        canvas.height = targetDimensions.height;
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;
        let drawWidth, drawHeight, drawX, drawY;
        if (imgAspect > canvasAspect) {
          drawHeight = canvas.height;
          drawWidth = drawHeight * imgAspect;
          drawX = (canvas.width - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = canvas.width;
          drawHeight = drawWidth / imgAspect;
          drawX = 0;
          drawY = (canvas.height - drawHeight) / 2;
        }
        ctx.fillStyle = "#1a1a1a";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        canvas.toBlob(
          async (blob) => {
            try {
              const reader = new FileReader();
              reader.onloadend = async () => {
                const uploadedUrl = await storageService.uploadImage(reader.result, user?.id);
                resolve(uploadedUrl || URL.createObjectURL(blob));
              };
              reader.readAsDataURL(blob);
            } catch {
              resolve(URL.createObjectURL(blob));
            }
          },
          "image/png",
        );
      };
      img.onerror = () =>
        reject(new Error("Failed to load image for resizing"));
      img.src = imageUrl;
    });
  };

  const handleDownload = async () => {
    if (!currentImage) return;
    setButtonLoading("download");
    await downloadImage(currentImage, `room-variation-${Date.now()}.png`);
    setButtonLoading(null);
  };

  const handleEditClick = () => {
    if (!currentImage) return;
    setButtonLoading("edit");
    setButtonLoading(null);
  };

  const handleDeleteClick = () => {
    if (!currentImage) return;
    setCurrentImage(null);
    setGenerationError(null);
  };

  const handleRegenerate = () => {
    if (!currentImage) return;
    setGenerationError(null);
    handleGenerate();
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    if (!roomImages || roomImages?.length === 0) {
      toast?.error(
        "Room image(s) are required. Please upload at least one room image.",
      );
      isGeneratingRef.current = false;
      return;
    }

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast?.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCost = getModelCreditCost(selectedModel);
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, creditCost);
    if (!creditCheck?.allowed) {
      toast?.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast?.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    setIsGenerating(true);
    startTimer();
    setCurrentImage(null);
    setGenerationError(null);
    abortControllerRef.current = new AbortController();
    try {
      const promptText = prompt?.trim();
      const roomType = (selectedRoomType === "--" || selectedRoomType === "Custom") ? "" : selectedRoomType;
      const roomSize = selectedRoomSize || "";
      const angleView = selectedAngleView === "--" ? "" : selectedAngleView;
      const angleShortcut = angleViewShortcut || "";
      const dimensions = getImageDimensions();
      const roomSizeDescriptionMap = {
        small: "up to 12 m² — compact, intimate proportions",
        medium: "13–25 m² — standard residential room proportions",
        large: "26–40 m² — spacious, open proportions",
        "extra-large": "40+ m² — expansive, grand proportions",
      };
      const roomSizeDescription = roomSizeDescriptionMap[roomSize] || "";

      const roomVariationSystemPrompt = `Role: You are an elite interior design creative director working within an interior design platform, connected to Gemini 3.1 Flash Image Preview image generation model for room variation visualization. You possess an aesthetically compelling, non-conventional yet classy perspective on interior design — your task is to re-imagine, not reproduce.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.

Intro: /room-variation-generator is intended to receive one original interior room image from the user and generate a completely new, creatively re-interpreted variation of that room. The variation must use ONLY the existing furniture, materials, objects, and textiles visible in the original uploaded image — nothing is added, nothing is removed. The AI must act as a top-tier designer who brings new life into the original room by crafting a completely new arrangement, composition, and spatial order using exclusively the props from the original image. The result must feel like a fresh, winning re-interpretation — better planned, more beautiful, more spatially elegant — while remaining faithful to the exact inventory of items visible in the source image.

Task: Analyze the original uploaded room image. Identify every piece of furniture, every material, every object, every textile visible in it. Then re-imagine the room composition — new furniture placement, new spatial flow, new focal points, new groupings — using only and exactly those identified elements. The variation must demonstrate mastery and creativity: out-of-the-box arrangement insights, unconventional but classy positioning, a compelling new perspective on how the same props can transform a room's character. The result must be a hyper-realistic render that looks like a professionally photographed interior, not a copy of the original but a bold, elegant re-interpretation of it. The room must feel spatially logical and physically correct according to the normal human world.

Creative direction principles:
Re-arrange, never replicate — the variation must feel like a completely different room that shares the same inventory.
Prioritize spatial flow and visual hierarchy — create new focal points, new sight lines, new conversation areas or functional zones.
Demonstrate designer-level taste — groupings should feel intentional, not random. Every placement choice should serve both function and beauty.
Surprise with elegance — the user should look at the result and think "I never would have thought to arrange it this way, but it's beautiful."
Maintain physical plausibility — furniture must sit on floors, objects must rest on surfaces, nothing floats or clips through walls.

High-quality keywords to maintain in every generation: re-imagined interior design variation, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera, interior design magazine editorial quality

Temperature: 1 (default Gemini).

You will receive these inputs from the user, and you need to send them to Gemini API in one call, in the following importance hierarchy from one to seven:

1. roomImages (REQUIRED) — the original interior room image uploaded by the user. This is the single source of truth. Every piece of furniture, every material, every object, every textile visible in these images is the complete and exclusive inventory for the variation. Identify all props precisely. Do not add any item not visible in this image. Do not remove any item visible in this image. Re-arrange all of them into a new, creatively superior composition.

2. selectedRoomSize — the user's chosen room size category. This defines the physical volume the generated variation must visually represent:
'small': up to 12 m² — compact, intimate proportions
'medium': 13–25 m² — standard residential room proportions
'large': 26–40 m² — spacious, open proportions
'extra-large': 40+ m² — expansive, grand proportions. The generated image must visually correspond to these real-world dimensions. Walls, ceiling height, floor area, and spatial depth must feel true to the stated size. Furniture scale must be proportionally correct within the chosen room volume.${
        roomSize
          ? `
   SELECTED ROOM SIZE: ${roomSize} — ${roomSizeDescription}`
          : ""
      }

3. prompt — include the user's text literally, word for word. If the user provides specific creative direction, arrangement preferences, or focal point requests, honor them while staying within the constraint of using only original image props.${
        promptText
          ? `
   USER PROMPT TEXT: ${promptText}`
          : ""
      }

4. selectedRoomType — include as spatial context and functional confirmation only, if not '--'. This confirms the room's purpose (bedroom, living room, kitchen, etc.) so the variation respects functional logic — beds belong in bedrooms, dining tables serve dining areas, etc.${
        roomType
          ? `
   ROOM TYPE: ${roomType}`
          : ""
      }

5. angleViewShortcut — if 'frontal', render the variation as a frontal camera perspective view. If 'top-down', render as a top-down drone view (90° overhead) showing the full room layout from above.${
        angleShortcut
          ? `
   ANGLE VIEW SHORTCUT: ${angleShortcut}`
          : ""
      }

6. selectedAngleView — more specific angle specification from the full dropdown list. Include only if not '--'. If both angleViewShortcut and selectedAngleView are provided, selectedAngleView takes priority as the more specific instruction.${
        angleView
          ? `
   SELECTED ANGLE VIEW: ${angleView}`
          : ""
      }

7. selectedImageSize — extract ratio strictly from what the user selected in the UI. If Auto, pass the aspect ratio of roomImages. If a specific size is selected, pass that value exactly. Do not hardcode any dimensions.
   REQUIRED OUTPUT DIMENSIONS: ${dimensions.width}x${dimensions.height} pixels (${selectedImageSize})

8. selectedModel — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.

CRITICAL DIMENSION REQUIREMENT: The output image MUST be generated in the exact dimensions specified: ${dimensions.width}x${dimensions.height} pixels. Do not use the aspect ratio from any uploaded images. The user has specifically selected ${selectedImageSize} format and this must be respected exactly.

STRICT RULES:
Do not add any furniture, objects, materials, textiles, light fixtures, or decorative elements not visible in the original roomImages. The original image is a closed inventory — nothing enters, nothing leaves.
Do not remove any item visible in the original roomImages. Every prop must appear in the variation, re-arranged into a new composition.
Do not use any style labels unless the user explicitly wrote them in prompt. Let the visual props define the style naturally.
Do not replicate the original arrangement. The variation must be a genuinely new composition — different furniture positions, different spatial relationships, different focal points.
Room size is binding — the visual volume of the generated room must match the selectedRoomSize category. Do not generate a visually large room when 'small' is selected, or vice versa.
Maintain physical and spatial plausibility at all times. Furniture must respect gravity, wall boundaries, door clearances, and walkable pathways. The room must function as a real human space.
If any input is missing or '--', skip it silently without generating filler content.
No explanations, no formatting, no headings in output.`;

      const baseInstruction = "Generate a completely new, creatively re-interpreted variation of the uploaded room. Use ONLY the existing furniture, materials, objects, and textiles visible in the original image — nothing is added, nothing is removed. Re-arrange all elements into a new, superior composition. Render with hyper realism and photorealism quality.";
      const fullPrompt = `${baseInstruction}\n\n${roomVariationSystemPrompt}`;

      let generatedImageUrl;

      if (isFluxModel(selectedModel)) {
        generatedImageUrl = await generateWithFlux({
          modelName: selectedModel,
          directPromptText: fullPrompt,
          userPromptText: promptText,
          referenceImages: roomImages,
          roomType: roomType,
          selectedAngleShot: angleView || angleShortcut,
          selectedRoomSize: roomSize,
          imageSize: selectedImageSize,
          signal: abortControllerRef.current.signal,
        });
      } else {
        generatedImageUrl = await generateRoomVariation({
          roomImages,
          selectedRoomSize: roomSize,
          userPromptText: promptText,
          selectedRoomType: roomType,
          angleViewShortcut: angleShortcut,
          selectedAngleView: angleView,
          imageSize: selectedImageSize,
          modelName: selectedModel,
          dimensions,
          toolSystemPrompt: fullPrompt,
          signal: abortControllerRef.current.signal,
        });
      }

      const resizedImageUrl = await resizeImageToCorrectDimensions(
        generatedImageUrl,
        dimensions,
      );
      setCurrentImage(resizedImageUrl);
      setGeneratedImages((prev) => [resizedImageUrl, ...prev]);
      setLibraryAssets((prev) => [...prev, resizedImageUrl]);
      clearTimer();
      setIsGenerating(false);
      setGenerationError("");
      toast?.success("Room variation generated successfully!");
      showSaveModal(resizedImageUrl, location.pathname);
      // Save to database
      await saveGenerationToDatabase(resizedImageUrl, prompt?.trim() || '', currentUser);
      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));
    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('cancelled')) {
        setCancelled();
      } else {
        console.error("Error generating room variation:", error);
        setFailed();
        setGenerationError(error?.message || "Failed to generate room variation");
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  const saveGenerationToDatabase = async (imageUrl, promptText, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `${selectedRoomType || 'Room Variation'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: promptText,
          thumbnail: imageUrl,
          tool_type: 'room-variation',
          tool_name: 'Room Variation Generator',
          status: 'completed',
          tags: [selectedRoomType, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        image_url: imageUrl,
        prompt: promptText,
        is_favorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        model_used: selectedModel,
        image_size: selectedImageSize || 'Auto',
        room_type: selectedRoomType || 'Room',
        style: 'Room Variation',
        resolution: selectedImageSize || 'Auto',
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...roomImages, imageUrl],
        additionalParams: {
          prompt: promptText,
          roomSize: selectedRoomSize,
          angleViewShortcut,
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const urls = designs.map((d) => d.imageUrl).filter(Boolean);
        setGeneratedImages(urls);
        setCurrentImage(urls[0]);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setSelectedRoomType(metadata.roomType);
          if (metadata.angleView && metadata.angleView !== '--') setSelectedAngleView(metadata.angleView);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          if (metadata?.additionalParams?.prompt) setPrompt(metadata.additionalParams.prompt);
          if (metadata?.additionalParams?.roomSize) setSelectedRoomSize(metadata.additionalParams.roomSize);
          if (metadata?.additionalParams?.angleViewShortcut) setAngleViewShortcut(metadata.additionalParams.angleViewShortcut);
        }
        if (designs[0]?.prompt && !prompt) setPrompt(designs[0].prompt);
      }
    };
    loadProjectData();
  }, []);

  useEffect(() => {
    if (promptRef?.current) {
      promptRef.current.style.height = "auto";
      promptRef.current.style.height = promptRef?.current?.scrollHeight + "px";
    }
  }, [prompt]);

  // Close aspect ratio dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (aspectRatioRef.current && !aspectRatioRef.current.contains(e.target)) {
        setIsAspectRatioOpen(false);
      }
      if (resolutionRef.current && !resolutionRef.current.contains(e.target)) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePromptChange = (e) => {
    const value = e?.target?.value;
    if (value?.length <= MAX_PROMPT_LENGTH) setPrompt(value);
  };

  // ── Shared UI sub-components ──────────────

  const isAtImageLimit = roomImages.length >= MAX_IMAGES;

  const PinterestButton = ({ mobile = false }) => (
    <button
      onClick={() => setShowPinterestModal(true)}
      disabled={isAtImageLimit}
      className={`w-full glassmorphic-button rounded-2xl flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
        mobile ? "py-2.5 px-4" : "py-3 px-4"
      }`}
    >
      <Icon name="Link" size={mobile ? 16 : 18} className="text-white" />
      <span
        className={`text-white font-medium ${mobile ? "text-xs" : "text-sm"}`}
      >
        Connect Pinterest
      </span>
    </button>
  );

  const ImageActionButtons = ({ size = 18 }) => (
    <div className="absolute bottom-4 right-4 flex gap-2">
      {[
        { key: "download", icon: "Download", handler: handleDownload },
        { key: "delete", icon: "Trash2", handler: handleDeleteClick },
        { key: "regen", icon: "RefreshCw", handler: handleRegenerate },
      ].map(({ key, icon, handler }) => (
        <button
          key={key}
          onClick={handler}
          disabled={!currentImage || buttonLoading !== null || isGenerating}
          className="p-2.5 rounded-xl bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {buttonLoading === key || (key === "regen" && isGenerating) ? (
            <Icon
              name="Loader"
              size={size}
              className="text-white animate-spin"
            />
          ) : (
            <Icon name={icon} size={size} className="text-white" />
          )}
        </button>
      ))}
    </div>
  );

  const UploadSection = ({ mobile = false }) => (
    <>
      <label className={`block ${isAtImageLimit ? "cursor-not-allowed" : "cursor-pointer"}`}>
        <input
          type="file"
          accept="image/png,image/jpeg"
          onChange={handleRoomImageUpload}
          className="hidden"
          disabled={isAtImageLimit}
        />
        <div
          className={`glassmorphic-upload-zone rounded-2xl text-center border-2 border-dashed border-transparent transition-all duration-300 ${
            isAtImageLimit
              ? "border-white/10 opacity-50"
              : "border-white/20 hover:border-orange-500/40"
          } ${mobile ? "p-5" : "p-8"}`}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
          onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-orange-500/50');
            const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
            if (imageUrl) {
              setRoomImages((prev) => prev.includes ? (prev.includes(imageUrl) ? prev : [...prev, imageUrl]) : [imageUrl]);
              return;
            }
          }}
        >
          <Icon
            name="Image"
            size={mobile ? 32 : 40}
            className={`mx-auto mb-3 ${isAtImageLimit ? "text-white/30" : "text-orange-500"}`}
          />
          <p className="text-white/70 text-sm">
            {isAtImageLimit ? `Maximum ${MAX_IMAGES} image reached` : "Upload image PNG or JPG"}
          </p>
        </div>
      </label>
      {roomImages?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {roomImages?.map((img, idx) => (
            <UploadedImageThumb key={idx} src={img} alt={`Room ${idx + 1}`} onRemove={() => handleRemoveRoomImage(idx)} imgClass={mobile ? "max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" : "max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"} height="h-auto" previewWidth="w-[200px]" />
          ))}
        </div>
      )}
    </>
  );

  const RoomSizeGrid = ({ mobile = false }) => (
    <div className="space-y-2">
      {[roomSizes.slice(0, 2), roomSizes.slice(2, 4)].map((row, ri) => (
        <div key={ri} className="grid grid-cols-2 gap-2">
          {row.map((rs) => (
            <button
              key={rs.id}
              onClick={() => setSelectedRoomSize(rs.id)}
              className={`rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all ${
                mobile ? "py-2.5 px-3" : "py-3 px-4"
              } ${
                selectedRoomSize === rs.id
                  ? "bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]"
                  : "bg-white/5 border border-white/10 hover:border-orange-500/30"
              }`}
            >
              <span className="text-white font-semibold text-xs">
                {rs.label}
              </span>
              <span className="text-white/90 text-[10px]">{rs.size}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );

  const renderAngleViewSection = ({ mobile = false, angleToggleRef, angleToggleGlowStyle, roomTypeRef, roomTypeGlowStyle, angleViewRef, angleViewGlowStyle }) => (
    <>
      {/* Common Angle Views Toggle + Room Type — same row, equal halves */}
      <div className={mobile ? "space-y-3 mb-3" : "grid grid-cols-2 gap-3 mb-4"}>
        {/* Common Angle Views Toggle */}
        <div ref={angleToggleRef} className="rounded-xl" style={angleToggleGlowStyle}>
          <label className={`text-white/70 mb-2 block ${mobile ? "text-[10px]" : "text-[12.6px]"}`}>Common angle views</label>
          <div className={`flex items-center justify-start ${mobile ? "gap-1.5 py-2.5" : "gap-2 py-3"}`}>
            <span className={`transition-colors ${mobile ? "text-[10px]" : "text-xs"} ${angleViewShortcut === 'frontal' ? 'text-white' : 'text-white/50'}`}>
              Frontal view
            </span>
            <button
              onClick={() => handleAngleViewShortcut(angleViewShortcut === 'frontal' ? 'top-down' : 'frontal')}
              className={`relative ${mobile ? "w-12 h-6" : "w-14 h-7"} rounded-full transition-all duration-300 flex-shrink-0 ${angleViewShortcut ? 'bg-orange-500' : 'bg-white/20'}`}
            >
              <div className={`absolute top-0.5 ${mobile ? "w-5 h-5" : "w-6 h-6"} bg-white rounded-full shadow-md transition-transform duration-300 ${angleViewShortcut === 'frontal' ? 'left-0.5' : 'right-0.5'}`} />
            </button>
            <span className={`transition-colors ${mobile ? "text-[10px]" : "text-xs"} ${angleViewShortcut === 'top-down' ? 'text-white' : 'text-white/50'}`}>
              Top-down view
            </span>
          </div>
        </div>
        {/* Room Type */}
        <div ref={roomTypeRef} className="rounded-xl" style={roomTypeGlowStyle}>
        <RoomTypeDropdown
          value={selectedRoomType}
          onChange={(val) => {
            setSelectedRoomType(val);
            if (val === 'Custom') {
              setTimeout(() => {
                if (promptRef?.current) {
                  promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  promptRef.current.focus();
                }
              }, 80);
            }
          }}
          mobile={mobile}
          showCustomOption
        />
        </div>
      </div>
      {/* Angle View Dropdown */}
      <div ref={angleViewRef} className="rounded-xl" style={angleViewGlowStyle}>
      <AngleViewDropdown
        value={selectedAngleView}
        onChange={(val) => {
          setSelectedAngleView(val);
          if (val === 'Straight-On Eye-Level View') {
            setAngleViewShortcut('frontal');
          } else if (val === 'Top-Down Flat View (90°)') {
            setAngleViewShortcut('top-down');
          } else {
            setAngleViewShortcut(null);
          }
        }}
        label={
          <span className="flex items-center gap-1.5">
            Angle view
            <span className="relative group/tip">
              <Icon name="Info" size={12} className="text-white/40 cursor-help" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 rounded-lg text-[10px] text-white/80 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none" style={{ background: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
                For more distinctive results, choose a different angle from the original image
              </span>
            </span>
          </span>
        }
      />
      </div>
    </>
  );

  const renderGeneratePanel = ({ mobile = false, modelRef, modelGlowStyle, aspectRatioGuideRef, aspectRatioGlowStyle, resolutionGuideRef, resolutionGlowStyle, promptGuideRef, promptGlowStyle, generateBtnRef, generateBtnGlowStyle }) => (
    <>
      <div className={mobile ? "space-y-3" : "grid grid-cols-2 gap-3"}>
        <div ref={modelRef} className="rounded-xl" style={modelGlowStyle}>
          <ModelSelectionDropdown
            value={selectedModel}
            onChange={setSelectedModel}
            mobile={mobile}
          />
        </div>
        <div ref={(node) => { aspectRatioRef.current = node; if (aspectRatioGuideRef) aspectRatioGuideRef.current = node; }} className="rounded-xl" style={aspectRatioGlowStyle}>
          <label
            className={`text-white/70 mb-2 block ${mobile ? "text-[10px]" : "text-[12.6px]"}`}
          >
            Aspect Ratio
          </label>
          <div className="relative">
            <button
              onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
              className={`w-full glassmorphic-select bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all ${mobile ? "rounded-xl px-3 py-2.5" : "rounded-2xl px-4 py-3"}`}
            >
              <div className="flex items-center gap-3">
                <AspectRatioIcon
                  ratio={selectedImageSize}
                  size={mobile ? 14 : 18}
                  className="text-orange-500"
                />
                <span className={`text-white ${mobile ? "text-xs" : "text-sm"}`}>
                  {selectedImageSize}
                </span>
              </div>
              <Icon
                name={isAspectRatioOpen ? "ChevronUp" : "ChevronDown"}
                size={mobile ? 14 : 16}
                className="text-white/50"
              />
            </button>
            {isAspectRatioOpen && (
              <div
                className={`absolute z-50 w-full bottom-full border border-white/10 shadow-2xl overflow-hidden ${mobile ? "mb-1 rounded-xl" : "mb-2 rounded-2xl"}`}
                style={{ backgroundColor: "rgba(0, 0, 0, 0.95)", backdropFilter: "blur(8px)" }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {aspectRatios?.map((ratio) => (
                  <button
                    key={ratio?.value}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedImageSize(ratio?.value);
                      setIsAspectRatioOpen(false);
                    }}
                    className={`w-full flex items-center justify-between transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${mobile ? "px-3 py-2.5" : "px-4 py-3"} ${
                      selectedImageSize === ratio?.value
                        ? "bg-orange-500/20 hover:bg-orange-500/30"
                        : "hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <AspectRatioIcon
                        ratio={ratio?.value}
                        size={mobile ? 14 : 18}
                        className={
                          selectedImageSize === ratio?.value
                            ? "text-orange-500"
                            : "text-white/50"
                        }
                      />
                      <span
                        className={`${mobile ? "text-xs" : "text-sm"} ${
                          selectedImageSize === ratio?.value
                            ? "text-white font-medium"
                            : "text-white/70"
                        }`}
                      >
                        {ratio?.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`${mobile ? "mt-2" : "mt-3 grid grid-cols-2 gap-3"}`}>
        <div ref={(node) => { resolutionRef.current = node; if (resolutionGuideRef) resolutionGuideRef.current = node; }} className="rounded-xl" style={resolutionGlowStyle}>
          {!isFluxModel(selectedModel) && (
          <>
          <label
            className={`text-white/70 mb-2 block ${mobile ? "text-[10px]" : "text-[12.6px]"}`}
          >
            Resolution
          </label>
          <div className="relative">
            <button
              onClick={() => setIsResolutionOpen(!isResolutionOpen)}
              className={`w-full glassmorphic-select bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all ${mobile ? "rounded-xl px-3 py-2.5" : "rounded-2xl px-4 py-3"}`}
            >
              <span className={`text-white font-medium ${mobile ? "text-xs" : "text-sm"}`}>{selectedResolution}</span>
              <div className="flex items-center gap-2">
                <span className={`text-white/50 ${mobile ? "text-[10px]" : "text-xs"}`}>
                    {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                  </span>
                <Icon name={isResolutionOpen ? "ChevronUp" : "ChevronDown"} size={mobile ? 14 : 16} className="text-white/50" />
              </div>
            </button>
            {isResolutionOpen && (
              <div
                className={`absolute z-50 w-full bottom-full border border-white/10 shadow-2xl overflow-hidden ${mobile ? "mb-1 rounded-xl" : "mb-2 rounded-2xl"}`}
                style={{ backgroundColor: "rgba(0, 0, 0, 0.95)", backdropFilter: "blur(8px)" }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {RESOLUTION_OPTIONS.map((resKey) => {
                  const dims = getResolutionDimensions(resKey);
                  return (
                    <button
                      key={resKey}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedResolution(resKey);
                        setIsResolutionOpen(false);
                      }}
                      className={`w-full flex items-center justify-between transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${mobile ? "px-3 py-2.5" : "px-4 py-3"} ${
                        selectedResolution === resKey
                          ? "bg-orange-500/20 hover:bg-orange-500/30"
                          : "hover:bg-white/5"
                      }`}
                    >
                      <span className={`${mobile ? "text-xs" : "text-sm"} ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                        {resKey}
                      </span>
                      <span className={`${mobile ? "text-[10px]" : "text-xs"} ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                          {dims.width}×{dims.height}
                        </span>
                    </button>
                  );
                })}
              </div>
            )}
            </div>
          </>
          )}
        </div>
        {!mobile && <div></div>}
      </div>

      <div ref={promptGuideRef} className={`rounded-xl ${mobile ? "mt-2" : "mt-3"}`} style={promptGlowStyle}>
        <label
          className={`text-white/70 mb-2 block ${mobile ? "text-[10px]" : "text-[12.6px]"}`}
        >
          Prompt:
        </label>
        {selectedRoomType === 'Custom' && (
          <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">
            <span>✏️</span> Describe the room type you want in the prompt below
          </p>
        )}
        <textarea
          ref={promptRef}
          value={prompt}
          onChange={handlePromptChange}
          placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request'}
          rows={5}
          className={`w-full glassmorphic-input rounded-2xl text-white bg-white/5 transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] focus:outline-none ${mobile ? "px-3 py-2.5 text-sm" : "px-4 py-3"} ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
          style={{ height: "120px" }}
        />
        <div className="text-right mt-1">
          <span
            className={`text-xs ${prompt?.length >= MAX_PROMPT_LENGTH ? "text-red-400" : "text-white/50"}`}
          >
            {prompt?.length?.toLocaleString()} /{" "}
            {MAX_PROMPT_LENGTH?.toLocaleString()}
          </span>
        </div>
      </div>

      <div ref={generateBtnRef} className={`rounded-xl ${mobile ? "mt-2" : "mt-3"}`} style={generateBtnGlowStyle}>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`w-full glassmorphic-button rounded-2xl text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${mobile ? "py-2.5 text-sm" : "py-3"}`}
        >
          {isGenerating ? (
            <>
              <Icon
                name="Loader"
                size={mobile ? 16 : 18}
                className="animate-spin"
              />{" "}
              Generating...
            </>
          ) : (
            <>
              <Icon name="Sparkles" size={mobile ? 16 : 18} /> Generate Image
            </>
          )}
        </button>
      </div>

      {isGenerating && (
        <button
          onClick={handleCancelGeneration}
          className={`w-full glassmorphic-button ${mobile ? "rounded-xl" : "rounded-2xl"} py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors`}
        >
          <Icon name="X" size={16} />
          Cancel
        </button>
      )}

      <p
        className={`text-white/50 text-center ${mobile ? "text-[10px] mt-2" : "text-xs mt-3"}`}
      >
        Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}
      </p>
    </>
  );

  // ── Render ────────────────────────────────
  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Toaster position="top-center" />
      <Header />
      <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
        <div className="max-w-[1400px] mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate("/main-dashboard")}
              className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-white hidden md:block">
                Room Variation Generator
              </h1>
              <h1 className="text-xl font-bold text-white md:hidden">
                Room Variation Generator
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openGuide()}
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                >
                  <Icon name="HelpCircle" size={18} />
                  Virtual Tool Guide
                </button>
                <button
                  onClick={() => openGuide()}
                  className="md:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                >
                  <Icon name="HelpCircle" size={16} />
                  Guide
                </button>
                <button
                  onClick={() => navigate("/project-history")}
                  className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform whitespace-nowrap flex-shrink-0"
                >
                  <Icon name="History" size={18} />
                  <span className="text-sm font-medium">View Projects</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── Desktop Layout ── */}
          <div className="hidden md:flex gap-4">
            {/* Left Panel */}
            <div className="w-[380px] flex-shrink-0 space-y-4">
              {/* Pinterest */}
              <div ref={pinterestDesktopRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, padding: showGuide && guideStep === 1 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 1 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-3xl p-6">
                <h3 className="text-[12.6px] font-medium text-white mb-4">
                  Connect Pinterest Boards
                </h3>
                <PinterestButton />
              </div>
              </div>
              {/* Upload */}
              <div ref={uploadDesktopRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, padding: showGuide && guideStep === 2 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 2 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-3xl p-6">
                <h3 className="text-[12.6px] font-medium text-white mb-2">
                  Room image(s)<span className="text-red-500">*</span>
                </h3>
                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${isAtImageLimit ? "text-red-400" : "text-white/70"}`}>
                    {roomImages.length} / {MAX_IMAGES} images uploaded
                  </span>
                </div>
                <UploadSection />
              </div>
              </div>
              {/* Room Size */}
              <div ref={roomSizeDesktopRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, padding: showGuide && guideStep === 3 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 3 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-3xl p-6">
                <h3 className="text-[12.6px] font-medium text-white mb-4">
                  Room size
                </h3>
                <RoomSizeGrid />
              </div>
              </div>
              <div ref={assetLibraryDesktopRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, padding: showGuide && guideStep === 4 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 4 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <AssetLibraryPanel
                libraryAssets={libraryAssets}
                setLibraryAssets={setLibraryAssets}
                generatedImages={generatedImages}
                setGeneratedImages={setGeneratedImages}
                currentProjectId={currentProjectId}
                onImageDeleted={(remaining) => {
                  if (!isGenerating) setCurrentImage(remaining[0] || null);
                }}
              />
              </div>
            </div>

            {/* Center Panel */}
            <div className="flex-1 space-y-4">
              {/* Result */}
              <div ref={resultDesktopRef} className="glassmorphic-card rounded-3xl p-4 relative" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 13 ? 'relative' : undefined, zIndex: showGuide && guideStep === 13 ? 201 : undefined, padding: showGuide && guideStep === 13 && guideVisible ? 'calc(16px + 10px)' : undefined, margin: showGuide && guideStep === 13 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 13 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div className="flex justify-end items-center mb-3 px-2">
                  <span className="text-white/70 text-[12.6px]">Result</span>
                </div>
                <div className="relative rounded-2xl overflow-hidden bg-black/40 h-[500px]">
                  {isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon
                          name="Loader"
                          size={48}
                          className="text-orange-500 mx-auto mb-3 animate-spin"
                        />
                        <p className="text-white text-sm font-medium">
                          Generating your room variation...
                        </p>
                        <p className="text-white/60 text-xs mt-1">
                          This may take a moment
                        </p>
                      </div>
                    </div>
                  ) : currentImage ? (
                    <img
                      src={currentImage}
                      alt="Generated room variation"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-white/50 text-sm">
                          No image generated yet
                        </p>
                        <p className="text-white/30 text-xs mt-1">
                          Upload a room image and click Generate
                        </p>
                      </div>
                    </div>
                  )}
                  {generationError && !isGenerating && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center max-w-md px-4">
                        <Icon
                          name="AlertCircle"
                          size={48}
                          className="text-red-500 mx-auto mb-3"
                        />
                        <p className="text-white text-sm font-medium mb-2">
                          Generation Failed
                        </p>
                        <p className="text-white/70 text-xs">
                          {generationError}
                        </p>
                      </div>
                    </div>
                  )}
                  <ImageActionButtons size={18} />
                </div>
              </div>
              {/* Room Type, Angle View, Model & Generate */}
              <div className="glassmorphic-card rounded-3xl p-4 space-y-4">
                {renderAngleViewSection({ angleToggleRef: angleToggleDesktopRef, angleToggleGlowStyle: { position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, padding: showGuide && guideStep === 5 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 5 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, roomTypeRef: roomTypeDesktopRef, roomTypeGlowStyle: { position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, padding: showGuide && guideStep === 6 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 6 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, angleViewRef: angleViewDesktopRef, angleViewGlowStyle: { position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, padding: showGuide && guideStep === 7 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 7 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' } })}
                {renderGeneratePanel({ modelRef: modelDesktopRef, modelGlowStyle: { position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, padding: showGuide && guideStep === 8 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 8 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, aspectRatioGuideRef: aspectRatioDesktopRef, aspectRatioGlowStyle: { position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, padding: showGuide && guideStep === 9 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 9 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, resolutionGuideRef: resolutionDesktopRef, resolutionGlowStyle: { position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, padding: showGuide && guideStep === 10 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 10 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, promptGuideRef: promptDesktopRef, promptGlowStyle: { position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, padding: showGuide && guideStep === 11 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 11 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, generateBtnRef: generateBtnDesktopRef, generateBtnGlowStyle: { position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, padding: showGuide && guideStep === 12 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 12 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' } })}
              </div>
            </div>
          </div>

          {/* ── Mobile Layout ── */}
          <div className="md:hidden space-y-4">
            <div ref={pinterestMobileRef} className="rounded-2xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, padding: showGuide && guideStep === 1 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 1 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div className="glassmorphic-card rounded-2xl p-4">
              <h3 className="text-[12.6px] font-medium text-white mb-3">
                Connect Pinterest Boards
              </h3>
              <PinterestButton mobile />
            </div>
            </div>
            <div ref={resultMobileRef} className="glassmorphic-card rounded-2xl p-3 relative" style={{ borderRadius: '1rem', position: showGuide && guideStep === 13 ? 'relative' : undefined, zIndex: showGuide && guideStep === 13 ? 201 : undefined, padding: showGuide && guideStep === 13 && guideVisible ? 'calc(12px + 10px)' : undefined, margin: showGuide && guideStep === 13 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 13 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="flex justify-end items-center mb-2 px-1">
                <span className="text-white/70 text-xs">Result</span>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-black/40 h-[280px]">
                {isGenerating ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Icon
                        name="Loader"
                        size={36}
                        className="text-orange-500 mx-auto mb-2 animate-spin"
                      />
                      <p className="text-white text-xs font-medium">
                        Generating...
                      </p>
                    </div>
                  </div>
                ) : currentImage ? (
                  <img
                    src={currentImage}
                    alt="Generated room variation"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-white/50 text-xs">
                        No image generated yet
                      </p>
                      <p className="text-white/30 text-xs mt-1">
                        Upload a room image and click Generate
                      </p>
                    </div>
                  </div>
                )}
                {generationError && !isGenerating && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center px-4">
                      <Icon
                        name="AlertCircle"
                        size={36}
                        className="text-red-500 mx-auto mb-2"
                      />
                      <p className="text-white text-xs font-medium">Failed</p>
                    </div>
                  </div>
                )}
                <ImageActionButtons size={14} />
              </div>
            </div>
            <div ref={uploadMobileRef} className="rounded-2xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, padding: showGuide && guideStep === 2 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 2 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div className="glassmorphic-card rounded-2xl p-4">
              <h3 className="text-[12.6px] font-medium text-white mb-2">
                Room image(s)<span className="text-red-500">*</span>
              </h3>
              <div className="text-left mb-2">
                <span className={`text-sm font-medium ${isAtImageLimit ? "text-red-400" : "text-white/70"}`}>
                  {roomImages.length} / {MAX_IMAGES} images uploaded
                </span>
              </div>
              <UploadSection mobile />
            </div>
            </div>
            <div ref={roomSizeMobileRef} className="rounded-2xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, padding: showGuide && guideStep === 3 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 3 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div className="glassmorphic-card rounded-2xl p-4">
              <h3 className="text-[12.6px] font-medium text-white mb-3">
                Room size
              </h3>
              <RoomSizeGrid mobile />
            </div>
            </div>
            <div ref={assetLibraryMobileRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, padding: showGuide && guideStep === 4 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 4 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <AssetLibraryPanel
              libraryAssets={libraryAssets}
              setLibraryAssets={setLibraryAssets}
              generatedImages={generatedImages}
              setGeneratedImages={setGeneratedImages}
              currentProjectId={currentProjectId}
              onImageDeleted={(remaining) => {
                if (!isGenerating) setCurrentImage(remaining[0] || null);
              }}
            />
            </div>
            <div className="glassmorphic-card rounded-2xl p-3 space-y-3">
              {renderAngleViewSection({ mobile: true, angleToggleRef: angleToggleMobileRef, angleToggleGlowStyle: { position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, padding: showGuide && guideStep === 5 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 5 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, roomTypeRef: roomTypeMobileRef, roomTypeGlowStyle: { position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, padding: showGuide && guideStep === 6 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 6 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, angleViewRef: angleViewMobileRef, angleViewGlowStyle: { position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, padding: showGuide && guideStep === 7 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 7 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' } })}
              {renderGeneratePanel({ mobile: true, modelRef: modelMobileRef, modelGlowStyle: { position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, padding: showGuide && guideStep === 8 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 8 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, aspectRatioGuideRef: aspectRatioMobileRef, aspectRatioGlowStyle: { position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, padding: showGuide && guideStep === 9 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 9 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, resolutionGuideRef: resolutionMobileRef, resolutionGlowStyle: { position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, padding: showGuide && guideStep === 10 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 10 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, promptGuideRef: promptMobileRef, promptGlowStyle: { position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, padding: showGuide && guideStep === 11 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 11 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }, generateBtnRef: generateBtnMobileRef, generateBtnGlowStyle: { position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, padding: showGuide && guideStep === 12 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 12 && guideVisible ? '-10px' : undefined, boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' } })}
            </div>
          </div>
        </div>
      </main>

      {/* Pinterest Modal */}
      {showPinterestModal && (
        <PinterestModal
          onClose={() => setShowPinterestModal(false)}
          onSelectImage={handlePinterestImageSelect}
        />
      )}

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
        <div
          className="fixed inset-0 z-[200]"
          onClick={closeGuide}
        >
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        </div>

        {/* Tooltip bubble */}
        <div
          className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
          style={(() => {
            if (guideAnimating) {
              return {
                ...guideBubblePosRef.current,
                opacity: 0,
                transform: 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            const desktopRefs = [pinterestDesktopRef.current, uploadDesktopRef.current, roomSizeDesktopRef.current, assetLibraryDesktopRef.current, angleToggleDesktopRef.current, roomTypeDesktopRef.current, angleViewDesktopRef.current, modelDesktopRef.current, aspectRatioDesktopRef.current, resolutionDesktopRef.current, promptDesktopRef.current, generateBtnDesktopRef.current, resultDesktopRef.current];
            const mobileRefs = [pinterestMobileRef.current, uploadMobileRef.current, roomSizeMobileRef.current, assetLibraryMobileRef.current, angleToggleMobileRef.current, roomTypeMobileRef.current, angleViewMobileRef.current, modelMobileRef.current, aspectRatioMobileRef.current, resolutionMobileRef.current, promptMobileRef.current, generateBtnMobileRef.current, resultMobileRef.current];
            const desktopRef = desktopRefs[guideStep - 1];
            const mobileRef = mobileRefs[guideStep - 1];
            const ref = window.innerWidth >= 768 ? desktopRef : mobileRef;
            if (!ref) return { top: '50%', left: '50%' };
            const rect = ref.getBoundingClientRect();
            const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
            if (guideStep >= 5) {
              const pos = { top: rect.top + rect.height / 2, left: rect.left - bubbleWidth - 32 };
              guideBubblePosRef.current = pos;
              return {
                ...pos,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            const pos = { top: rect.top + rect.height / 2, left: rect.right + 32 };
            guideBubblePosRef.current = pos;
            return {
              ...pos,
              opacity: guideVisible ? 1 : 0,
              transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
              transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            };
          })()}
        >
          <div className="pointer-events-auto">
            {/* Arrow - conditional direction */}
            {guideStep >= 5 ? (
              <>
                <div
                  className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            ) : (
              <>
                <div
                  className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderRight: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderRight: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            )}
            <div
              className="rounded-2xl p-5 text-center"
              style={{
                background: 'rgb(24, 24, 24)',
                border: '1px solid rgba(254, 131, 56, 0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)',
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'scale(1)' : 'scale(0.97)',
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            >
              <div
                className="flex items-center justify-center gap-2 mb-2"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                }}
              >
                <Icon name={[null, "Link", "Upload", "Maximize2", "FolderOpen", "ToggleRight", "Home", "Video", "Cpu", "RectangleHorizontal", "ScanLine", "Type", "Sparkles", "Image"][guideStep] || "Settings"} size={18} className="text-white/50" />
                <span className="text-sm font-semibold text-white">Step {guideStep}</span>
              </div>
              <p
                className="text-sm text-white/80 leading-relaxed"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s',
                }}
              >
                {guideStep === 1 ? (
                  <>Link your <span className="text-white font-medium italic">Pinterest account</span> to import saved pins (images) directly into the uploading zones. Allows you to pull reference images without downloading them manually.</>
                ) : guideStep === 2 ? (
                  <>Upload an existing interior image — a completed render, a project photograph, or any room reference. The AI will use this as the basis to generate new <span className="text-white font-medium italic">design variations</span> while preserving the spatial composition. Without a room image, the image generation will not start.</>
                ) : guideStep === 3 ? (
                  <>The <span className="text-white font-medium italic">room size</span> which you expect to receive in the final generation.</>
                ) : guideStep === 4 ? (
                  <>Generated images which you chose to store inside Roomwork.ai can be <span className="text-white font-medium italic">dragged and dropped</span> into 'Room image(s)'.</>
                ) : guideStep === 5 ? (
                  <>A shortcut toggle between the two most frequently used <span className="text-white font-medium italic">camera perspectives</span>. Select one for quick access without navigating the full Angle view dropdown.</>
                ) : guideStep === 6 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">type of room</span> you want to generate.</>
                ) : guideStep === 7 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">camera angle perspective</span> for your room generation.</>
                ) : guideStep === 8 ? (
                  <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                ) : guideStep === 9 ? (
                  <>Image ratio: <span className="text-white font-medium italic">vertical or horizontal</span> (choose the precise ratio according to the dropdown menu icons). Different ratios allow you to explore different room compositions.</>
                ) : guideStep === 10 ? (
                  <>A dropdown selector that allows you to choose the output image <span className="text-white font-medium italic">resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                ) : guideStep === 11 ? (
                  <>An optional text field for <span className="text-white font-medium italic">additional instructions</span>: specify changes to style, color palette, materials, or any design direction you want the variation to follow.</>
                ) : guideStep === 12 ? (
                  <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you're ready! Choose only the fields that are relevant to your needs.</>
                ) : guideStep === 13 ? (
                  <>Receive the generated image in the <span className="text-white font-medium italic">'Result'</span> window. Use the buttons in the bottom right corner of the Result window to download, delete, or regenerate the image using the exact same settings.</>
                ) : null}
              </p>
              <button
                onClick={() => guideStep < totalGuideSteps ? nextGuideStep() : closeGuide()}
                className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
                }}
              >
                {guideStep < totalGuideSteps ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default RoomVariationGenerator;
